import { useEffect, useState } from "react"
import CountrySelector from "components/Shared/HeaderFilters/AssociatedCountriesSelector"
import Select from "react-select"
import "react-dates/initialize"
import { Input, Checkbox, Modal, TextInput, FormLabel } from "@wfp/ui"
import { SingleDatePicker } from "react-dates"
import { Moment } from "moment"
import { CommonGetOptions, SurveyMetaData } from "interfaces/dataInterfaces"
import useCommon from "hooks/useCommon"
import { renderMonthElement } from "utils/dateHelper"
import { useGetHHXlsFormQuery, useGetAssessmentStatusesQuery, useGetPhasesQuery } from "services/household/survey"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { useAddOrganizationMutation, useGetOrganizationsQuery } from "services/household/organizations"
import { useAddAttributeMutation, useGetAttributesQuery } from "services/household/attributes"
import { useGetModalitiesQuery } from "services/household/modalities"
import { useGetAllCategoriesQuery } from "services/household/categories"
import { useGetSubCategoriesQuery } from "services/household/subCategories"

interface Props {
  values: SurveyMetaData
  setValues: (value: SurveyMetaData) => void
  adm0code?: number
  xlsFormId?: number
  startDate?: Moment | null
  endDate?: Moment | null
  setAdm0code?: any
  setXlsFormId?: any
  setStartDate?: any
  setEndDate?: any
  hide?: string[]
}

export default function SurveyMeta({
  values,
  setValues,
  startDate,
  endDate,
  adm0code,
  xlsFormId,
  setStartDate,
  setEndDate,
  setXlsFormId,
  setAdm0code,
  hide = [],
}: Props) {
  const { showSnackBar } = useCommon()
  const [xlsFormsIsArchived, setxlsFormsIsArchived] = useState<boolean>(false)
  const [showModals, setShowModals] = useState({ addNewOrg: false, addNewAttribute: false })
  const [focused, setFocused] = useState({ startDate: false, endDate: false })
  const [metaOpts, setMetaOpts] = useState<any>({})
  const [inputs, setInputs] = useState<{ orgName: string | null; attrName: string | null }>({
    orgName: null,
    attrName: null,
  })
  const [options, setOptions] = useState<CommonGetOptions[]>([])
  const { data: sCategories } = useGetAllCategoriesQuery()
  const { data: sAttributes } = useGetAttributesQuery()
  const { data: sModalities } = useGetModalitiesQuery()
  const { data: sStatuses } = useGetAssessmentStatusesQuery()
  const { data: sPhases } = useGetPhasesQuery()
  const { data: sOrganizations } = useGetOrganizationsQuery()
  const [addAttribute] = useAddAttributeMutation()
  const [addOrganization] = useAddOrganizationMutation()
  const { data: sSubCategories } = useGetSubCategoriesQuery(
    { categoryId: values.surveyCategoryId },
    { skip: !values.surveyCategoryId },
  )

  const { data: xlsFormList, isLoading: loadingXlsFormList } = useGetHHXlsFormQuery(
    {
      adm0Code: adm0code ?? 0,
      xlsFormSubCategoryId: values.surveySubCategoryId || 0,
    },
    { skip: !adm0code || !values.surveySubCategoryId },
  )

  const addNewOrganization = async () => {
    try {
      const name: string = inputs.orgName || ""
      if (!name) return
      if (name.length > 50) {
        showSnackBar("Organization name characters must be less than 50.", "error")
        return
      }
      await addOrganization({ name }).unwrap()

      setShowModals({ ...showModals, addNewOrg: false })
    } catch (err) {
      const error = err as FetchBaseQueryError
      showSnackBar(
        (error?.data as { message: string })?.message ?? "An error occurred while adding new organization",
        "error",
      )
    }
  }

  const addNewAttribute = async () => {
    try {
      const name: string = inputs.attrName || ""
      if (!name) return
      if (name.length > 50) {
        showSnackBar("Attribute name characters must be less than 50.", "error")
        return
      }

      const response = await addAttribute({ name })
      // @ts-expect-error
      if (response?.error?.status === 422) {
        showSnackBar("Attribute with this name already exist!", "error")
        return
      }
      setShowModals({ ...showModals, addNewAttribute: false })
    } catch (error) {
      showSnackBar("Error occurred while adding new attribute, please try again later!", "error")
    }
  }

  useEffect(() => {
    // setValues({...values, surveyPhaseId: 0})
    if (Number(values.surveyAssessmentStatusId) !== 1) return // if status is not completed then do not mark phase complete
    setValues({ ...values, surveyPhaseId: 4 }) // phase 4 means completed
  }, [values.surveyAssessmentStatusId])

  useEffect(() => {
    const categories = sCategories?.map(({ name, value }) => ({ label: name, value }))
    const attributes = sAttributes?.map(({ name, value }) => ({ label: name, value }))
    const modalities = sModalities?.map(({ name, value }) => ({ label: name, value }))
    const statuses = sStatuses?.map(({ name, value }) => ({ label: name, value }))
    const phases = sPhases?.map(({ name, value }) => ({ label: name, value }))
    const subCategories = sSubCategories?.map(({ name, value }) => ({ label: name, value }))
    const organizations = sOrganizations?.map(({ name, value }) => ({ label: name, value })) || []
    setMetaOpts({ categories, modalities, attributes, statuses, phases, subCategories, organizations })
  }, [sCategories, sModalities, sAttributes, sStatuses, sPhases, sSubCategories, sOrganizations])

  useEffect(() => {
    if (inputs.orgName) {
      const newAddedOrg = sOrganizations?.find(({ name }) => name === inputs.orgName)

      if (newAddedOrg?.value)
        setValues({ ...values, organizationIds: [...values.organizationIds, Number(newAddedOrg.value)] })
      setInputs({ ...inputs, orgName: "" })
    }
    if (inputs.attrName) {
      const newAddedAttr = sAttributes?.find(({ name }) => name === inputs.attrName)

      if (newAddedAttr?.value)
        setValues({ ...values, surveyAttributeIds: [...values.surveyAttributeIds, Number(newAddedAttr.value)] })
      setInputs({ ...inputs, attrName: "" })
    }
  }, [sOrganizations, sAttributes])

  useEffect(() => {
    xlsFormId && setXlsFormId(0)
    if (values.surveySubCategoryId) {
      setOptions(xlsFormList?.filter(item => item.xlsFormsIsArchived === xlsFormsIsArchived) ?? [])
    } else {
      setOptions([])
    }
  }, [values.surveySubCategoryId, xlsFormList])

  return (
    <div className="space-y-5">
      <div className="flex gap-3">
        {!hide.includes("countryId") && (
          <div className="w-50 country-full">
            <CountrySelector
              allowAll={false}
              isClearable={false}
              label="Country"
              onSelect={country => {
                setAdm0code(country?.value)
                setXlsFormId(undefined) // reset XLSForm selection
              }}
              value={adm0code}
            />
            {!adm0code && (
              <p style={{ color: "var(--support-01)", fontSize: 12, fontWeight: 600 }}>
                <span>Country field is required.</span>
              </p>
            )}
          </div>
        )}
        {!hide.includes("category") && (
          <div className="w-50">
            <label className="wfp--label">Category</label>
            <Select
              key={metaOpts?.categories?.length}
              placeholder="Select a survey category"
              classNamePrefix="select"
              value={metaOpts?.categories?.find(({ value }) => Number(value) === values.surveyCategoryId)}
              options={metaOpts.categories}
              onChange={item => {
                setValues({ ...values, surveyCategoryId: Number(item?.value), surveySubCategoryId: null })
                setXlsFormId(undefined)
                setOptions([])
              }}
            />
            {!values.surveyCategoryId && (
              <p style={{ color: "var(--support-01)", fontSize: 12, fontWeight: 600 }}>
                <span>Category is required.</span>
              </p>
            )}
          </div>
        )}
      </div>
      <div className="flex gap-3">
        {!hide.includes("category") && (
          <div className="w-50">
            <label className="wfp--label">Type</label>
            <Select
              key={metaOpts?.subCategories?.length}
              placeholder="Select a survey type"
              classNamePrefix="select"
              value={metaOpts?.subCategories?.find(({ value }) => Number(value) === values.surveySubCategoryId)}
              options={metaOpts.subCategories}
              onChange={item => {
                setValues({ ...values, surveySubCategoryId: Number(item?.value) })
                setXlsFormId(undefined)
              }}
            />
            {!values.surveySubCategoryId && (
              <p style={{ color: "var(--support-01)", fontSize: 12, fontWeight: 600 }}>
                <span>Type is required.</span>
              </p>
            )}
          </div>
        )}
        {!hide.includes("xlsFormId") && (
          <div className="w-50">
            <div className="d-flex justify-between">
              <FormLabel>XLSForm Schema</FormLabel>
              <div className="d-flex wfp--label primary-color !mb-1.5">
                <Checkbox
                  wrapperClassName="ms-4 !mb-0 flex-none"
                  labelText="Show archived"
                  onChange={e => {
                    setxlsFormsIsArchived(e.target.checked)
                    setOptions(xlsFormList?.filter(item => item.xlsFormsIsArchived === e.target.checked) ?? [])
                    setXlsFormId(0)
                  }}
                  value="xlsFormsIsArchived"
                  id="xlsFormsIsArchived"
                />
              </div>
            </div>

            <Select
              key={xlsFormId}
              isLoading={loadingXlsFormList}
              placeholder="Select XLSForm"
              classNamePrefix="select"
              options={options}
              getOptionLabel={option => option.name}
              getOptionValue={option => option.value}
              value={options?.find(item => Number(item.value) === xlsFormId)}
              onChange={item => setXlsFormId(Number(item?.value))}
            />
            {!xlsFormId && (
              <p style={{ color: "var(--support-01)", fontSize: 12, fontWeight: 600 }}>
                <span>Select first country, category and type. XLSForm field is required.</span>
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <div className="w-50">
          <label className="wfp--label">Modality</label>
          <Select
            key={metaOpts?.modalities?.length}
            placeholder="Select a survey modality"
            classNamePrefix="select"
            value={metaOpts?.modalities?.find(({ value }) => Number(value) === values.surveyModalityId)}
            options={metaOpts.modalities}
            onChange={item => setValues({ ...values, surveyModalityId: Number(item?.value) })}
          />
          {!values.surveyModalityId && (
            <p style={{ color: "var(--support-01)", fontSize: 12, fontWeight: 600 }}>
              <span>Modality is required.</span>
            </p>
          )}
        </div>

        <div className="w-50">
          <div className="d-flex" style={{ marginBottom: "0.1rem" }}>
            <label className="wfp--label">Attribute</label>
            <label
              className="wfp--label ml-auto primary-color cursor-pointer"
              onClick={() => setShowModals({ ...showModals, addNewAttribute: true })}
            >
              Add new +
            </label>
          </div>
          <Select
            placeholder="Select a survey attribute"
            classNamePrefix="select"
            isMulti
            value={metaOpts?.attributes?.filter(({ value }) => values.surveyAttributeIds?.includes(Number(value)))}
            options={metaOpts.attributes}
            onChange={items => setValues({ ...values, surveyAttributeIds: items.map(item => Number(item.value)) })}
          />
        </div>
      </div>

      <div className="flex gap-3">
        <div className="w-33">
          <label className="wfp--label">Status</label>
          <Select
            key={metaOpts?.statuses?.length}
            placeholder="Select a survey status"
            classNamePrefix="select"
            value={metaOpts?.statuses?.find(({ value }) => Number(value) === values.surveyAssessmentStatusId)}
            options={metaOpts.statuses}
            onChange={item => {
              setValues({ ...values, surveyAssessmentStatusId: Number(item?.value), surveyPhaseId: null })
            }}
          />
          {!values.surveyAssessmentStatusId && (
            <p style={{ color: "var(--support-01)", fontSize: 12, fontWeight: 600 }}>
              <span>Status is required.</span>
            </p>
          )}
        </div>

        <div className="w-33">
          <label className="wfp--label">Phase</label>
          <Select
            key={metaOpts?.phases?.length}
            isDisabled={values.surveyAssessmentStatusId !== 3}
            placeholder="Select a survey phase"
            classNamePrefix="select"
            options={
              values.surveyAssessmentStatusId === 3
                ? metaOpts.phases?.filter((v: any) => v.value !== "4")
                : metaOpts.phases
            }
            value={
              metaOpts?.phases?.find(
                (item: { label: string; value: string }) => Number(item.value) === values.surveyPhaseId,
              ) ?? null
            }
            onChange={item => setValues({ ...values, surveyPhaseId: Number(item?.value) })}
          />
          {values.surveyAssessmentStatusId === 2 || values.surveyPhaseId ? null : (
            <p style={{ color: "var(--support-01)", fontSize: 12, fontWeight: 600 }}>
              <span>Phase is required.</span>
            </p>
          )}
        </div>

        <div className="w-33">
          <div className="d-flex" style={{ marginBottom: "0.1rem" }}>
            <label className="wfp--label">Organisations</label>
            <label
              className="wfp--label  ml-auto primary-color cursor-pointer"
              onClick={() => setShowModals({ ...showModals, addNewOrg: true })}
            >
              Add new +
            </label>
          </div>
          <Select
            placeholder="Select a organization"
            classNamePrefix="select"
            isMulti
            value={metaOpts?.organizations?.filter(({ value }) => values.organizationIds?.includes(Number(value)))}
            options={metaOpts.organizations}
            onChange={items => setValues({ ...values, organizationIds: items.map(item => Number(item.value)) })}
          />
        </div>
      </div>

      <div className="flex gap-3">
        <TextInput
          name="surveyName"
          labelText="Survey Name"
          placeholder="Type survey name..."
          type="text"
          onChange={e => setValues({ ...values, surveyName: e.target.value || "" })}
          invalid={!values.surveyName}
          invalidText="Survey name field is required."
          value={values?.surveyName}
          className="border-gray-300 rounded-md text-base"
        />

        {!hide.includes("startDate") && (
          <div>
            <Input labelText="Start Date" invalidText="A valid value is required">
              {() => (
                <SingleDatePicker
                  displayFormat="DD/MM/YYYY"
                  showDefaultInputIcon
                  renderMonthElement={renderMonthElement}
                  inputIconPosition="after"
                  onDateChange={newDate => setStartDate(newDate)}
                  focused={focused.startDate}
                  onFocusChange={({ focused: isFocus }) => setFocused({ startDate: isFocus, endDate: false })}
                  date={startDate as any}
                  id="start_date"
                  isOutsideRange={m => {
                    if (!endDate) return false
                    return m.unix() > endDate.unix()
                  }}
                />
              )}
            </Input>
            {!startDate && (
              <p style={{ color: "var(--support-01)", fontSize: 12, fontWeight: 600 }}>
                <span>Start date is required.</span>
              </p>
            )}
          </div>
        )}

        <Checkbox
          className="mt-3"
          checked={values.surveyHouseholdIsContinuousMonitoring}
          id="surveyHouseholdIsContinuousMonitoring"
          labelText="Continuous Monitoring"
          value="continuous"
          onChange={() =>
            setValues({
              ...values,
              surveyHouseholdIsContinuousMonitoring: !values.surveyHouseholdIsContinuousMonitoring,
            })
          }
        />

        {!hide.includes("endDate") && (
          <Input invalidText="A valid value is required" labelText="End Date">
            {() => (
              <SingleDatePicker
                displayFormat="DD/MM/YYYY"
                showDefaultInputIcon
                renderMonthElement={renderMonthElement}
                inputIconPosition="after"
                onDateChange={newDate => setEndDate(newDate)}
                focused={focused.endDate}
                onFocusChange={({ focused: isSingleFocus }) => setFocused({ startDate: false, endDate: isSingleFocus })}
                date={endDate as any}
                id="end_date"
                isOutsideRange={m => {
                  if (!startDate) return false
                  return m.unix() < startDate.unix()
                }}
              />
            )}
          </Input>
        )}
      </div>

      <Modal
        open={showModals.addNewOrg}
        modalHeading="Add new organization"
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestClose={() => setShowModals({ ...showModals, addNewOrg: false })}
        onSecondarySubmit={() => setShowModals({ ...showModals, addNewOrg: false })}
        onRequestSubmit={addNewOrganization}
      >
        <TextInput
          name="newOrganization"
          placeholder="Type new organization name..."
          type="text"
          style={{ fontSize: "0.875rem", borderRadius: 4 }}
          onChange={e => setInputs({ ...inputs, orgName: e.target.value || "" })}
          value={inputs.orgName ?? ""}
        />
      </Modal>

      <Modal
        open={showModals.addNewAttribute}
        modalHeading="Add new attribute"
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestClose={() => setShowModals({ ...showModals, addNewAttribute: false })}
        onSecondarySubmit={() => setShowModals({ ...showModals, addNewAttribute: false })}
        onRequestSubmit={addNewAttribute}
      >
        <TextInput
          name="newAttribute"
          placeholder="Type new attribute name..."
          type="text"
          style={{ fontSize: "0.875rem", borderRadius: 4 }}
          onChange={e => setInputs({ ...inputs, attrName: e.target.value || "" })}
        />
      </Modal>
    </div>
  )
}
